import { AnchorHTMLAttributes, MouseEvent, ReactNode, useEffect } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { ensureBlobUrls, selectBlobUrl } from "./attachementsSlice"

type BlobLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
    blobId: string
    children: ReactNode
}

/**
 * Renders a download/open link for a blob through a signed (SAS) read URL. The URL is
 * minted on mount so hover / middle-click work natively; if it has expired by the time
 * the user clicks, the click re-mints a fresh one and then opens it. Replaces the old
 * inline `${BLOB_API}/blobs/...` anchors.
 */
export const BlobLink = ({ blobId, children, onClick, target = "_blank", rel = "noreferrer", ...anchorProps }: BlobLinkProps) => {
    const dispatch = useAppDispatch()
    const url = useAppSelector(selectBlobUrl(blobId))

    useEffect(() => {
        if (blobId) {
            dispatch(ensureBlobUrls([blobId]))
        }
    }, [blobId, dispatch])

    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e)
        if (e.defaultPrevented) return

        // A fresh cached URL navigates natively (no popup-blocker risk). Otherwise the SAS
        // has expired or was never minted: stop navigation, re-mint, then open the result.
        if (!url) {
            e.preventDefault()
            dispatch(ensureBlobUrls([blobId]))
                .unwrap()
                .then(minted => {
                    const fresh = minted[blobId]
                    if (fresh) {
                        window.open(fresh, target, rel === "noreferrer" ? "noopener,noreferrer" : undefined)
                    }
                })
                .catch(() => { /* surfaced elsewhere; nothing actionable here */ })
        }
    }

    return (
        <a href={url ?? "#"} target={target} rel={rel} onClick={handleClick} {...anchorProps}>
            {children}
        </a>
    )
}

export default BlobLink
